import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import { compose } from 'utils/compose';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { Dialog, DialogContent, TextField, FormControlLabel, Checkbox } from '@mui/material';

import DialogHeader from './DialogHeader';
import { Hr } from 'components/DeliveryManager';
import { DialogActionButton, DialogFooter } from 'components/Dialog';
import { BusinessHours } from 'components/BusinessHours/BusinessHours';

import { updateDelivery } from 'utils/api';
import useSearchParams from 'hooks/useSearchParams';
import { ACCESS_DENIED_BY_DOORMAN, COMMERCIAL_BUILDING, DEFAULT_REQUEST_ERROR_TEXT } from 'constants/common';
import { createDefaultBusinessHours } from 'constants/businessHours';

const PREFIX = 'InstructionDialog';

const classes = {
  root: `${PREFIX}-root`,
  title: `${PREFIX}-title`,
  text: `${PREFIX}-text`,
  input: `${PREFIX}-input`,
  btn: `${PREFIX}-btn`,
  notice: `${PREFIX}-notice`,
  error: `${PREFIX}-error`,
  formControlLabel: `${PREFIX}-formControlLabel`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`&.${classes.root}`]: {
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(4),
    },
  },

  [`& .${classes.title}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '16px',
    fontWeight: 600,
    margin: '0 0 0.5rem 0',
  },

  [`& .${classes.text}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
    marginTop: 0,
  },

  [`& .${classes.input}`]: {
    color: '#7b7b7b',
    fontSize: '14px',
  },

  [`& .${classes.btn}`]: {
    borderRadius: '2rem',
    marginLeft: '0 !important',
  },

  [`& .${classes.notice}`]: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
  },

  [`& .${classes.error}`]: {
    margin: 0,
  },

  [`& .${classes.formControlLabel}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colorTokens.onSurfaceTextTitle,
  },
}));

function InstructionDialog({ store }) {
  const { trackingCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { deliveryStore } = store;
  const { delivery } = deliveryStore;
  const instruction = delivery?.delivery_instruction || '';
  const shipmentID = delivery?.shipment?.id;
  const haveRoamingPets = Boolean(delivery?.have_free_roaming_pet);
  const businessHours = deliveryStore?.businessHours ? deliveryStore.businessHours : createDefaultBusinessHours();
  const questionnaire = get(delivery, 'recipient-questionnaire');
  const addressType = get(questionnaire, 'address_type');
  const isBusiness = get(delivery, 'business');
  const reason = searchParams.get('reason');

  const formMethods = useForm({
    defaultValues: {
      delivery_instruction: instruction,
      have_free_roaming_pet: haveRoamingPets,
      business_hours: businessHours,
    },
  });
  const { formState, handleSubmit, control, setValue } = formMethods;
  const { errors } = formState;
  const error = get(errors, 'delivery_instruction.message', '');

  const handleCancel = () => {
    searchParams.set('dialog', 'closed');
    setSearchParams(searchParams);
  };

  useEffect(() => {
    setValue('business_hours', businessHours);
  }, [deliveryStore?.businessHours]);

  const onSubmit = (data) => {
    if (addressType === COMMERCIAL_BUILDING || isBusiness) {
      const businessHours = get(data, 'business_hours');
      const businessHoursData = businessHours.map((day) => {
        return {
          day: day.day,
          status: day.status,
          open: day.open,
          close: day.close,
        };
      });
      data.business_hours = JSON.stringify(businessHoursData);
    } else {
      delete data.business_hours;
    }
    updateDelivery(trackingCode, shipmentID, data)
      .then((response) => {
        if (response.status !== 200)
          return toast.error(<span className={classes.notice}>{response?.data?.message || DEFAULT_REQUEST_ERROR_TEXT}</span>, {
            hideProgressBar: true,
            theme: 'colored',
          });

        deliveryStore.getDelivery(trackingCode);
        handleCancel();
      })
      .catch((error) =>
        toast.error(<span className={classes.notice}>{error?.message || DEFAULT_REQUEST_ERROR_TEXT}</span>, {
          hideProgressBar: true,
          theme: 'colored',
        }),
      );
  };

  const getHeader = () => {
    if (reason === ACCESS_DENIED_BY_DOORMAN) {
      return 'Unsuccessful Delivery - Access Denied by Doorman';
    }
    return 'Delivery Instructions';
  };

  const getContent = () => {
    if (reason === ACCESS_DENIED_BY_DOORMAN) {
      return "We're sorry that we could not deliver your previous shipment. Help our drivers by providing any additional instructions to get your package to your address.";
    }
    return 'While Jitsu will attempt to complete your delivery change requests, you will not be able to update the scheduled delivery date.';
  };

  return (
    <StyledDialog open maxWidth="md" fullWidth className={classes.root}>
      <DialogHeader title={getHeader()} />
      <DialogContent>
        <p className={classes.text}>{getContent()}</p>
        <Hr />
        <p className={classes.title}>Delivery Instructions:</p>
        <Controller
          name="delivery_instruction"
          control={control}
          rules={{ minLength: { value: 3, message: 'This field should contain at least 3 characters' } }}
          render={({ field }) => (
            <TextField
              {...field}
              error={Boolean(error)}
              helperText={error}
              multiline
              fullWidth
              rows={5}
              size="small"
              variant="outlined"
              placeholder="Tell us more about where should we leave the package or tips to find your address!"
              slotProps={{
                input: { className: classes.input },
                formHelperText: { className: classes.error },
              }}
            />
          )}
        />
        <Controller
          name="have_free_roaming_pet"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              {...field}
              checked={field.value}
              label="I have free roaming pets"
              classes={{ label: classes.formControlLabel }}
              control={<Checkbox size="small" color="primary" />}
              data-testid="pet-aware-checkbox-dialog"
            />
          )}
        />
        {(addressType === COMMERCIAL_BUILDING || isBusiness) && (
          <FormProvider {...formMethods}>
            <BusinessHours edit={true} />
          </FormProvider>
        )}
      </DialogContent>
      <DialogFooter>
        <DialogActionButton size="medium" variant="outlined" color="primary" onClick={handleCancel} disableElevation>
          Cancel
        </DialogActionButton>
        <DialogActionButton size="medium" variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disableElevation>
          Save
        </DialogActionButton>
      </DialogFooter>
    </StyledDialog>
  );
}

export default compose(inject('store'), observer)(InstructionDialog);
